import { Component, signal, OnInit, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { SharedModule } from '../../../../zentility/_shared/shared.module';
import { BillAiFlowStore } from '../state/bill-ai-flow.store';
import { NewCustomerContact, NewCustomerDetails, NewCustomerPreview } from '../state/bill-ai-flow.models';
import { CustomerServiceV4 } from '../../../../zentility/_shared/_services/v4/customer-v4.service';
import { OnboardingLevel } from '../../../models';
import { catchError, debounceTime, map, switchMap, take, timeout } from 'rxjs/operators';
import { firstValueFrom, Observable, of, Subject } from 'rxjs';
import { PhoneMask } from '../../../../zentility/_shared/_enums/zen-masks.enum';
import { shouldAutoAcceptMatch } from '../../../utils/match-score.util';
import { SignupService } from '../../../../zentility/_shared/_zen-legacy-common/zen-common-services/tili-services/services/signup.service';
import { ValidPatterns } from '../../../../zentility/_shared/_zen-legacy-common/_models/common/email-pattern';
import { User } from '../../../../zentility/_shared/_zen-legacy-common/_models/user';

/** Outcome of a debounced primary-contact email lookup. */
type EmailLookupResult =
  | { kind: 'invalid' }
  | { kind: 'available' }
  | { kind: 'found'; user: User }
  | { kind: 'unavailable'; message: string }
  | { kind: 'error' };

/** Contact form fields that lock when auto-populated from an existing user.
 *  (title is also auto-populated but has no input in this step, so it's never locked.) */
type LockableContactField = 'firstName' | 'lastName' | 'phone';

@Component({
  selector: 'app-bill-ai-flow-intro-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatProgressSpinnerModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatRadioModule, SharedModule],
  templateUrl: './bill-ai-flow-intro-step.component.html',
  styleUrl: './bill-ai-flow-intro-step.component.scss'
})
export class BillAiFlowIntroStepComponent implements OnInit {
  phase = signal<'scanning' | 'results' | 'choice' | 'fast-tracking' | 'error'>('scanning');
  isCreatingCustomer = false;

  // Track whether user is using existing values or custom for name/address
  nameMode = signal<'existing' | 'custom'>('existing');
  addressMode = signal<'existing' | 'custom'>('existing');

  // Track the currently selected address label for dropdown sync
  private _selectedAddressLabel = signal<string>('');

  // Computed that auto-selects first address label if none selected
  selectedAddressLabel = computed(() => {
    const current = this._selectedAddressLabel();
    if (current) return current;

    // Auto-select first if in existing mode and addresses available
    if (this.addressMode() === 'existing' && this.availableBillingAddresses.length > 0) {
      return this.availableBillingAddresses[0].label;
    }
    return '';
  });

  // Phone mask for formatting
  phoneMask = PhoneMask;
  phoneUnmask = true;

  billCount: number;
  processedCount: number;
  isPortfolioLevel: boolean;
  contextLevel: OnboardingLevel;

  // Pre-computed scanning text (avoids method calls in template)
  scanningTitle: string;
  scanningLabel: string;
  scanningSubtext: string;

  // Pre-computed customer preview (avoids calling getNewCustomerPreview() in template)
  newCustomerPreview: NewCustomerPreview = { suggestedName: '', billingAddress: null, legalEntityNames: [], serviceAddressCount: 0, meterCount: 0, billCount: 0 };

  // Pre-computed extracted values for template (avoids method calls in templates)
  availableCustomerNames: string[] = [];
  availableBillingAddresses: { label: string; value: NewCustomerDetails }[] = [];

  // Fast track
  fastTrackAnalysis = computed(() => this.state.fastTrackAnalysis());
  fastTrackProgressMessage = signal('');
  fastTrackSkipCount = computed(() => {
    const analysis = this.state.fastTrackAnalysis();
    if (!analysis) return 0;
    return [analysis.canSkipLen, analysis.canSkipSa, analysis.canSkipMeter, analysis.canSkipBillReview]
      .filter(Boolean).length;
  });
  fastTrackEntityCount = computed(() => {
    const stats = this.state.stats();
    return stats.legalEntities.total + stats.serviceAddresses.total + stats.meters.total;
  });

  // Whether "Create New Customer" is selected
  isCreateNewSelected = computed(() => this.state.selectedCustomerId() === null);

  // Contact info from state
  contact = computed(() => this.state.newCustomerContact());

  // Check if contact info is valid for new customer
  isContactValid = computed(() => this.state.isNewCustomerContactValid());

  // ==========================================================================
  // Cross-customer primary contact email lookup (DEV-14748)
  // Email comes first: a valid email is checked against existing platform
  // users so an existing contact's details auto-populate instead of being
  // typed by hand. Mirrors the primary-contact email lookup in
  // ZenCustomerProfileDialogComponent (this step has no title input, so
  // title is stored but never locked).
  // ==========================================================================
  emailLookupStatus = signal<'idle' | 'checking' | 'linked' | 'unavailable'>('idle');
  emailLookupError = signal<string | null>(null);
  private lockedContactFields = signal<ReadonlySet<LockableContactField>>(new Set());

  firstNameLocked = computed(() => this.lockedContactFields().has('firstName'));
  lastNameLocked = computed(() => this.lockedContactFields().has('lastName'));
  phoneLocked = computed(() => this.lockedContactFields().has('phone'));

  private emailLookup$ = new Subject<string>();
  private emailPattern = new RegExp(ValidPatterns.EMAIL_PATTERN);

  private unavailableError = 'Unavailable';

  public state = inject(BillAiFlowStore);
  private customerService = inject(CustomerServiceV4);
  private signupSvc = inject(SignupService);

  constructor() {
    this.billCount = this.state.getBills().length;
    this.processedCount = this.state.getProcessedBills().length;
    this.contextLevel = this.state.getContext().level;
    this.isPortfolioLevel = this.contextLevel === 'PORTFOLIO';
    this.scanningTitle = this.buildScanningTitle();
    this.scanningLabel = this.buildScanningLabel();
    this.scanningSubtext = this.buildScanningSubtext();
    this.newCustomerPreview = this.state.getNewCustomerPreview();
    this.refreshExtractedValues();

    // No distinctUntilChanged here: onContactFieldChange moves the status to
    // 'checking' on every email edit, so suppressing an equal re-emission
    // would strand the status there; a re-run of the same email is cheap.
    this.emailLookup$
      .pipe(
        debounceTime(500),
        switchMap(email => this.lookupContactEmail(email)),
        takeUntilDestroyed()
      )
      .subscribe(result => this.applyEmailLookupResult(result));

    this.restoreLinkedContactLocks();
  }

  // Editable customer details
  customerDetails = computed(() => this.state.getEditableCustomerDetails());

  // Contact field change handlers
  onContactFieldChange(field: keyof NewCustomerContact, value: string): void {
    this.state.updateNewCustomerContact(field, value);
    if (field === 'email') {
      // Any existing link belongs to the previous email — drop it now, and
      // block Continue synchronously so the debounce window can't be raced
      // into submitting a stale userId (or skipping the lookup entirely).
      this.clearLinkedContact();
      this.emailLookupStatus.set('checking');
      this.emailLookupError.set(null);
      this.emailLookup$.next(value);
    }
  }

  /**
   * Check the email against existing platform users. When the email belongs to
   * an existing user (including a contact of another customer), the user record
   * is returned so their details can be auto-populated and linked via userId.
   * Side effect: sets emailLookupStatus to 'checking' (and clears the error)
   * as soon as a syntactically valid email is submitted.
   */
  private lookupContactEmail(email: string): Observable<EmailLookupResult> {
    if (!email?.trim() || !this.emailPattern.test(email.trim())) {
      return of({ kind: 'invalid' } as EmailLookupResult);
    }
    this.emailLookupStatus.set('checking');
    this.emailLookupError.set(null);
    return this.signupSvc.signupEmailCheck(email, null, true).pipe(
      timeout(10000),
      switchMap(res => {
        if (res.result) {
          return of({ kind: 'available' } as EmailLookupResult);
        }
        return this.signupSvc.signupRetrieveUserByEmail(email, null, true).pipe(
          timeout(10000),
          map(user => {
            if (user != null) {
              return { kind: 'found', user } as EmailLookupResult;
            }
            // Backend returns null when the account can't be linked as a
            // contact: admin/service accounts ('Unavailable') or an email
            // already tied to an existing customer account.
            const message = res.errorMessage === this.unavailableError
              ? 'This email is already in use and can\'t be added as a contact.'
              : 'A customer with this email already exists.';
            return { kind: 'unavailable', message } as EmailLookupResult;
          }),
          // The availability check already said the email is taken — degrade
          // to unavailable rather than fail open if the user fetch dies.
          catchError(err => {
            console.error('Primary contact user fetch failed:', err);
            return of({
              kind: 'unavailable',
              message: 'This email appears to be in use, but its details couldn\'t be retrieved. Please try again.'
            } as EmailLookupResult);
          })
        );
      }),
      // Fail open — a lookup outage shouldn't block customer creation
      catchError(err => {
        console.error('Primary contact email lookup failed; proceeding without duplicate check:', err);
        return of({ kind: 'error' } as EmailLookupResult);
      })
    );
  }

  private applyEmailLookupResult(result: EmailLookupResult): void {
    switch (result.kind) {
      case 'found': {
        const user = result.user;
        this.state.patchNewCustomerContact({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          phone: user.phone || '',
          title: user.title || '',
          userId: user.id
        });
        this.lockFieldsFromUser(user);
        this.emailLookupStatus.set('linked');
        this.emailLookupError.set(null);
        break;
      }
      case 'available':
        this.clearLinkedContact();
        this.emailLookupStatus.set('idle');
        this.emailLookupError.set(null);
        break;
      case 'unavailable':
        this.clearLinkedContact();
        this.emailLookupStatus.set('unavailable');
        this.emailLookupError.set(result.message);
        break;
      case 'invalid':
        this.clearLinkedContact();
        this.emailLookupStatus.set('idle');
        this.emailLookupError.set(null);
        break;
      case 'error':
        // Fail open for *blocking*, but never retain a link that belonged to
        // a previous email — proceed with a clean slate.
        this.clearLinkedContact();
        this.emailLookupStatus.set('idle');
        this.emailLookupError.set(null);
        break;
    }
  }

  /** Lock the fields that were populated from the existing user record. */
  private lockFieldsFromUser(user: User): void {
    const locked = new Set<LockableContactField>();
    if (user.firstName) locked.add('firstName');
    if (user.lastName) locked.add('lastName');
    if (user.phone) locked.add('phone');
    this.lockedContactFields.set(locked);
  }

  /** Unlink a previously matched user and clear the fields that came from it. */
  private clearLinkedContact(): void {
    if (this.contact()?.userId) {
      // title is auto-populated from the linked user but has no input in this
      // step, so it must always be cleared along with the link.
      const cleared: Partial<NewCustomerContact> = { userId: null, title: '' };
      for (const field of this.lockedContactFields()) {
        cleared[field] = '';
      }
      this.state.patchNewCustomerContact(cleared);
    }
    this.lockedContactFields.set(new Set());
  }

  /**
   * On restore (state persists across reloads), re-fetch the linked user so
   * auto-populated fields lock again — without overwriting anything the user
   * may have typed into fields the linked user record left empty. If the
   * link can no longer be confirmed (record deleted, email reassigned, or
   * the fetch failed), the stale userId is dropped rather than submitted.
   */
  private restoreLinkedContactLocks(): void {
    const saved = this.state.newCustomerContact();
    if (!saved?.userId || !saved.email) return;
    this.signupSvc.signupRetrieveUserByEmail(saved.email, null, true)
      .pipe(
        take(1),
        timeout(10000),
        map(user => ({ ok: true, user })),
        catchError(err => {
          console.error('Failed to re-verify linked primary contact on restore:', err);
          return of({ ok: false, user: null as User | null });
        }),
        takeUntilDestroyed()
      )
      .subscribe(({ ok, user }) => {
        // The advisor may have edited the email while this was in flight —
        // the live lookup pipeline owns the state in that case.
        if (this.contact()?.email !== saved.email) return;
        if (user?.id === saved.userId) {
          this.lockFieldsFromUser(user);
          this.emailLookupStatus.set('linked');
        } else if (ok) {
          // Confirmed stale — the user record changed since the session
          // was persisted, so drop the link rather than submit it.
          this.state.patchNewCustomerContact({ userId: null, title: '' });
        } else {
          // Transient failure — don't guess. Hand the email to the live
          // pipeline so the link settles through a real checking cycle.
          this.emailLookupStatus.set('checking');
          this.emailLookup$.next(saved.email);
        }
      });
  }

  // Customer details field change handlers
  onDetailsFieldChange(field: keyof NewCustomerDetails, value: string): void {
    this.state.updateNewCustomerDetails(field, value);
  }

  // Rebuild cached extracted values (call after bills or match data changes)
  private refreshExtractedValues(): void {
    this.availableCustomerNames = this.buildAvailableCustomerNames();
    this.availableBillingAddresses = this.buildAvailableBillingAddresses();
  }

  // Build available customer names from legal entities
  private buildAvailableCustomerNames(): string[] {
    const bills = this.state.getBills().filter(b => b.data);
    const names = new Set<string>();
    for (const bill of bills) {
      const name = bill.data?.legalEntity?.trim();
      if (name) {
        names.add(name);
      }
    }
    return Array.from(names).sort();
  }

  // Select a customer name from available options
  selectCustomerName(name: string): void {
    this.state.updateNewCustomerDetails('customerName', name);
  }

  // Switch name mode
  setNameMode(mode: 'existing' | 'custom'): void {
    this.nameMode.set(mode);
    if (mode === 'existing') {
      if (this.availableCustomerNames.length > 0) {
        this.selectCustomerName(this.availableCustomerNames[0]);
      }
    }
  }

  // Switch address mode
  setAddressMode(mode: 'existing' | 'custom'): void {
    this.addressMode.set(mode);
    if (mode === 'existing') {
      this.autoSelectFirstAddress();
    }
  }

  // Auto-select first available address and set the dropdown label
  private autoSelectFirstAddress(): void {
    if (this.availableBillingAddresses.length > 0) {
      const first = this.availableBillingAddresses[0];
      this.state.updateNewCustomerDetails('address1', first.value.address1);
      this.state.updateNewCustomerDetails('address2', first.value.address2);
      this.state.updateNewCustomerDetails('city', first.value.city);
      this.state.updateNewCustomerDetails('state', first.value.state);
      this.state.updateNewCustomerDetails('zip', first.value.zip);
      this._selectedAddressLabel.set(first.label);
    }
  }

  // Build available billing addresses from all LENs
  private buildAvailableBillingAddresses(): { label: string; value: NewCustomerDetails }[] {
    const bills = this.state.getBills().filter(b => b.data);
    const seen = new Set<string>();
    const addresses: { label: string; value: NewCustomerDetails }[] = [];

    for (const bill of bills) {
      const d = bill.data!;
      if (!d.billingAddressStreet1?.trim()) continue;

      const key = [
        d.billingAddressStreet1,
        d.billingAddressStreet2,
        d.billingAddressCity,
        d.billingAddressState,
        d.billingAddressZip
      ].join('|');

      if (seen.has(key)) continue;
      seen.add(key);

      const label = [
        d.billingAddressStreet1,
        d.billingAddressCity,
        d.billingAddressState,
        d.billingAddressZip
      ].filter(Boolean).join(', ');

      addresses.push({
        label,
        value: {
          customerName: '',
          address1: d.billingAddressStreet1 || '',
          address2: d.billingAddressStreet2 || '',
          city: d.billingAddressCity || '',
          state: d.billingAddressState || '',
          zip: d.billingAddressZip || ''
        }
      });
    }

    return addresses;
  }

  // Select a billing address by its label (from dropdown)
  selectBillingAddressByLabel(label: string): void {
    const found = this.availableBillingAddresses.find(a => a.label === label);
    if (found) {
      this.state.updateNewCustomerDetails('address1', found.value.address1);
      this.state.updateNewCustomerDetails('address2', found.value.address2);
      this.state.updateNewCustomerDetails('city', found.value.city);
      this.state.updateNewCustomerDetails('state', found.value.state);
      this.state.updateNewCustomerDetails('zip', found.value.zip);
      this._selectedAddressLabel.set(label);
    }
  }

  ngOnInit(): void {
    // Auto-scan on entry - behavior differs based on context level
    this.scanForMatches();
  }

  async scanForMatches(): Promise<void> {
    this.phase.set('scanning');

    try {
      const context = this.state.getContext();
      const customerId = context.customerId ? Number(context.customerId) : null;
      await this.state.runMatch(customerId);

      if (this.state.error()) {
        this.phase.set('error');
        return;
      }

      if (this.isPortfolioLevel) {
        // PORTFOLIO level: Show results with customer options inline
        this.phase.set('results');

        // Refresh preview now that matchRequest is available
        this.newCustomerPreview = this.state.getNewCustomerPreview();

        // Auto-select best match if very high confidence — uses the same
        // AUTO_ACCEPT_SCORE threshold as initializeSelections and the
        // fast-track analyzer to keep the three auto-accept paths aligned.
        const best = this.state.customerMatch()?.match;
        if (shouldAutoAcceptMatch(best) && this.state.selectedCustomerId() === null) {
          this.state.setSelectedCustomerId(best!.id);
          this.state.setSelectedCustomerLabel(best!.label);
        } else if (this.state.selectedCustomerId() === null) {
          // "Create New Customer" is the default selection — initialize extracted values
          this.initializeExtractedValues();
        }
      } else {
        // Non-portfolio levels: customer is already known from context
        this.state.completeStep('intro');
        this.state.completeStep('customer');

        // Pre-select entities based on context level
        this.applyContextSelections();

        // Analyze fast track eligibility
        this.state.analyzeFastTrack();
        const analysis = this.state.fastTrackAnalysis();

        if (analysis?.isEligible) {
          // Show choice panel
          this.phase.set('choice');
        } else {
          // No fast track available, proceed normally
          const nextStep = this.state.getNextStep();
          if (nextStep) {
            this.state.goToStep(nextStep);
          }
        }
      }
    } catch (err) {
      console.error('[BillAiFlowIntroStep] Fast-track bootstrap failed:', err);
      this.phase.set('error');
    }
  }

  /**
   * For non-PORTFOLIO levels, apply pre-selections based on context.
   * - At CUSTOMER level: customer is set, proceed to LEN matching
   * - At LEN level: customer + LEN are set, proceed to SA matching
   * - At SERVICE_ADDRESS level: customer + LEN + SA are set, proceed to meter matching
   */
  private applyContextSelections(): void {
    const context = this.state.getContext();
    const response = this.state.matchResponse();

    if (!response) return;

    // At LEN level and below, if we have a known lenId, pre-select it
    if (context.lenId && (context.level === 'LEN' || context.level === 'SERVICE_ADDRESS' || context.level === 'METER')) {
      // Find the LEN in the response that should be linked to context.lenId
      // Since we're adding to an existing LEN, mark all LENs in the response to link to the context lenId
      for (const len of response.legalEntities) {
        this.state.updateSelection(len.groupId, context.lenId);
      }
      this.state.completeStep('len');
    }

    // At SERVICE_ADDRESS level and below, if we have a known serviceAddressId, pre-select it
    if (context.serviceAddressId && (context.level === 'SERVICE_ADDRESS' || context.level === 'METER')) {
      // Pre-select all service addresses to link to the context serviceAddressId
      for (const len of response.legalEntities) {
        for (const sa of len.serviceAddresses) {
          this.state.updateSelection(sa.groupId, Number(context.serviceAddressId));
        }
      }
      this.state.completeStep('sa');
    }

    // At METER level, if we have a known meterId, pre-select it
    if (context.meterId && context.level === 'METER') {
      // Pre-select all meters to link to the context meterId — bills are being
      // added to a single known meter
      for (const len of response.legalEntities) {
        for (const sa of len.serviceAddresses) {
          for (const meter of sa.meters) {
            this.state.updateSelection(meter.groupId, Number(context.meterId));
          }
        }
      }
      this.state.completeStep('meter');
    }
  }

  // =========================================================================
  // Level-specific UI text
  // =========================================================================

  private buildScanningTitle(): string {
    switch (this.contextLevel) {
      case 'CUSTOMER':
        return 'Adding data to this customer';
      case 'LEN':
        return 'Adding addresses to this entity';
      case 'SERVICE_ADDRESS':
        return 'Adding meters to this address';
      case 'METER':
        return 'Adding bills to this meter';
      default:
        return 'Let\'s review your portfolio';
    }
  }

  private buildScanningLabel(): string {
    switch (this.contextLevel) {
      case 'CUSTOMER':
        return 'Scanning for existing entities';
      case 'LEN':
        return 'Scanning for existing addresses';
      case 'SERVICE_ADDRESS':
        return 'Scanning for existing meters';
      case 'METER':
        return 'Validating bill data';
      default:
        return 'Scanning for existing matches';
    }
  }

  private buildScanningSubtext(): string {
    switch (this.contextLevel) {
      case 'CUSTOMER':
        return 'Checking legal entities, service addresses, and meters';
      case 'LEN':
        return 'Checking service addresses and meters';
      case 'SERVICE_ADDRESS':
        return 'Checking meters and account numbers';
      case 'METER':
        return 'Checking for duplicate bills';
      default:
        return 'Checking customers, legal entities, and service addresses';
    }
  }

  treatAsNew(): void {
    this.state.setTreatAsNew(true);
    this.state.completeStep('intro');
    this.state.completeStep('customer');

    // Skip all matching steps, go directly to bill-review
    const steps = this.state.steps();
    for (const step of steps) {
      if (step.key !== 'bill-review' && step.key !== 'summary') {
        this.state.completeStep(step.key);
      }
    }
    this.state.goToStep('bill-review');
  }

  selectCustomer(id: number | string | null, label: string): void {
    this.state.setSelectedCustomerId(id);
    this.state.setSelectedCustomerLabel(label);

    // When "Create New Customer" is selected, auto-initialize extracted values
    if (id === null) {
      this.initializeExtractedValues();
    }
  }

  /**
   * Initialize extracted name and address when "Create New Customer" is selected.
   */
  private initializeExtractedValues(): void {
    // Refresh cached values in case bills data changed
    this.refreshExtractedValues();

    // Auto-select first name
    if (this.availableCustomerNames.length > 0) {
      this.state.updateNewCustomerDetails('customerName', this.availableCustomerNames[0]);
    }

    // Auto-select first address
    this.autoSelectFirstAddress();
  }

  async confirmCustomer(): Promise<void> {
    const custId = this.state.selectedCustomerId();

    if (custId === null) {
      // "Create New Customer" — validate contact info first
      if (!this.state.isNewCustomerContactValid()) {
        this.state.setError('Please enter primary contact information (name and a valid email)');
        return;
      }
      if (this.emailLookupStatus() === 'unavailable') {
        this.state.setError(this.emailLookupError() || 'This email can\'t be used for the primary contact.');
        return;
      }

      const details = this.state.getEditableCustomerDetails();
      this.state.setSelectedCustomerLabel(details.customerName || this.newCustomerPreview.suggestedName);

      try {
        this.isCreatingCustomer = true;
        this.state.setError(null);

        const request = this.state.getNewCustomerRequest();
        const result = await firstValueFrom(this.customerService.createCustomer(request));
        if (!result) {
          throw new Error('Customer creation returned no result');
        }

        this.state.setSelectedCustomerId(result.customerId);
        this.state.setCreatedCustomerUpfront(true);
        this.state.initializeSelectionsAllNew();
      } catch (err: any) {
        const errorMsg = err?.error?.message || err?.message || 'Failed to create customer';
        this.state.setError(errorMsg);
        return;
      } finally {
        this.isCreatingCustomer = false;
      }
    } else {
      // Existing customer selected — re-run match with customerId
      await this.state.runMatch(Number(custId));
      if (this.state.error()) return;
    }

    // Complete both intro and customer steps
    this.state.completeStep('intro');
    this.state.completeStep('customer');

    // Analyze fast track eligibility
    this.state.analyzeFastTrack();
    const analysis = this.state.fastTrackAnalysis();

    if (analysis?.isEligible) {
      this.phase.set('choice');
    } else {
      const next = this.state.getNextStep();
      if (next) {
        this.state.goToStep(next);
      }
    }
  }

  async chooseFastTrack(): Promise<void> {
    this.phase.set('fast-tracking');
    this.fastTrackProgressMessage.set('Matching everything for you...');
    try {
      await this.state.executeFastTrack();
    } catch (err) {
      console.error('Express import failed:', err);
      // Fall back to manual review so the user isn't stuck
      this.state.setFastTrackMode('review');
      const next = this.state.getNextStep();
      if (next) {
        this.state.goToStep(next);
      } else {
        this.phase.set('error');
      }
    }
  }

  chooseReviewEverything(): void {
    this.state.setFastTrackMode('review');
    const next = this.state.getNextStep();
    if (next) {
      this.state.goToStep(next);
    }
  }

  // Whether Continue button should be enabled (computed for template)
  canContinueResult = computed(() => {
    const custId = this.state.selectedCustomerId();
    // Must have a selection (undefined means nothing selected yet — but initial is null for "create new")
    if (custId === undefined) return false;
    // If creating new: valid contact info, and the email must not be
    // unavailable or still being checked (checking starts synchronously on
    // every email edit, so the debounce window can't be raced)
    if (custId === null) {
      const lookupStatus = this.emailLookupStatus();
      return this.state.isNewCustomerContactValid()
        && lookupStatus !== 'unavailable'
        && lookupStatus !== 'checking';
    }
    return true;
  });
}
